import type { TabItem } from '../types/tabs';

interface TabBarProps {
  tabs: TabItem[];
  activeTabId: string | null;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
}

export default function TabBar({ tabs, activeTabId, onTabClick, onTabClose }: TabBarProps) {
  const getTabIcon = (tab: TabItem) => {
    switch (tab.type) {
      case 'plan':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📋</div>;
      case 'table':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📄</div>;
      case 'flowsheet':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">🔗</div>;
      case 'predictor_model':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">⚙️</div>;
      case 'optimization':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">🎯</div>;
      case 'data':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📊</div>;
      case 'pdf':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📑</div>;
      case 'report':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📝</div>;
      case 'qms':
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">🔍</div>;
      default:
        return <div className="w-12 h-12 flex items-center justify-center leading-none text-4xl">📄</div>;
    }
  };

  // Separate plan tab from dynamic tabs
  const planTab = tabs.find(t => t.type === 'plan');
  const dynamicTabs = tabs.filter(t => t.type !== 'plan');

  return (
    <div className="flex items-center justify-center">
      <div
        className="flex items-center gap-0.5"
        style={{
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))'
        }}
      >
        {/* Plan tab - always first, no close button */}
        {planTab && (
          <>
            <button
              className={`w-12 h-12 flex items-center justify-center font-semibold transition-all duration-300 hover:scale-125 ${
                activeTabId === planTab.id
                  ? 'scale-125 opacity-100 [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.3))_drop-shadow(0_1px_2px_rgba(0,0,0,0.2))]'
                  : 'opacity-70 hover:opacity-100 hover:[filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.3))_drop-shadow(0_1px_2px_rgba(0,0,0,0.2))]'
              }`}
              title="Plan"
              onClick={() => onTabClick(planTab.id)}
            >
              {getTabIcon(planTab)}
            </button>
            {/* Separator */}
            {dynamicTabs.length > 0 && (
              <div className="w-px h-10 bg-gradient-to-b from-transparent via-gray-400/40 to-transparent mx-2" />
            )}
          </>
        )}

        {/* Dynamic tabs with close buttons */}
        {dynamicTabs.map((tab) => (
          <div
            key={tab.id}
            className="relative group"
          >
            <button
              className={`w-12 h-12 flex items-center justify-center font-semibold transition-all duration-300 hover:scale-125 ${
                activeTabId === tab.id
                  ? 'scale-125 opacity-100 [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.3))_drop-shadow(0_1px_2px_rgba(0,0,0,0.2))]'
                  : 'opacity-70 hover:opacity-100 hover:[filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.3))_drop-shadow(0_1px_2px_rgba(0,0,0,0.2))]'
              }`}
              title={tab.file_name || 'Untitled'}
              onClick={() => onTabClick(tab.id)}
            >
              {getTabIcon(tab)}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTabClose(tab.id);
              }}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-600 hover:scale-110 z-10 shadow-lg"
              title="Close tab"
              style={{ lineHeight: '1' }}
            >
              <span className="text-xs leading-none font-medium">×</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
